import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { billingAPI } from '../services/api';
import { Balance, billingVisible } from '../lib/billing';
import { AuthContext } from './AuthContext';

/**
 * BillingContext — the ONE balance the whole app agrees on (design D6/D15:
 * the sidebar row, the collapsed-rail ring, the mobile strip and the composer
 * hint must never disagree).
 *
 * Scope (design D7): the balance follows the org that owns the open project;
 * with no project open it is the caller's personal org. Pages that know their
 * project call setScopeProjectId (the sidebar derives it from /apps/:id, the
 * task page from its task). The server resolves the org; the client never
 * names one.
 *
 * Freshness: refreshed on scope change, on turn terminal events (callers
 * invoke refresh()), and polled every 30s ONLY while a turn runs
 * (setTurnRunning). Never polled at rest.
 *
 * Errors are silent: the state table says a balance that fails to load is
 * hidden, never a scary error.
 */

export const TURN_POLL_MS = 30_000;

interface BillingContextValue {
  balance: Balance | null;
  loading: boolean;
  /** Pricing chrome is on for this viewer (gate + unmetered, lib/billing.ts). */
  visible: boolean;
  scopeProjectId: string | null;
  setScopeProjectId: (id: string | null) => void;
  setTurnRunning: (running: boolean) => void;
  refresh: () => Promise<Balance | null>;
}

const noop = async () => null;
const DEFAULT: BillingContextValue = {
  balance: null,
  loading: false,
  visible: false,
  scopeProjectId: null,
  setScopeProjectId: () => {},
  setTurnRunning: () => {},
  refresh: noop,
};

export const BillingContext = createContext<BillingContextValue>(DEFAULT);

/** Safe outside a provider (existing component tests render without one). */
export const useBilling = (): BillingContextValue => useContext(BillingContext);

export const BillingProvider: React.FC<{ children: React.ReactNode; fetchBalance?: (projectId: string | null) => Promise<Balance> }> = ({
  children,
  fetchBalance,
}) => {
  const auth = useContext(AuthContext) as any;
  const user = auth ? auth.user : null;
  const token = auth ? auth.token : null;
  // The balance AND the scope it was fetched for: on a scope change the old
  // org's dollars must not show beside the new project until its own lands.
  const [held, setHeld] = useState<{ balance: Balance | null; scope: string | null } | null>(null);
  const [loading, setLoading] = useState(false);
  const [scopeProjectId, setScopeProjectIdState] = useState<string | null>(null);
  const [turnRunning, setTurnRunning] = useState(false);
  const scopeRef = useRef<string | null>(null);
  scopeRef.current = scopeProjectId;
  const seq = useRef(0);

  const refresh = useCallback(async (): Promise<Balance | null> => {
    if (!token) return null;
    const mine = ++seq.current;
    const scope = scopeRef.current;
    setLoading(true);
    try {
      const b = fetchBalance
        ? await fetchBalance(scope)
        : ((await billingAPI.balance(scope)).data as Balance);
      // A slower answer for an older scope never overwrites a newer one.
      if (mine === seq.current) setHeld({ balance: b, scope });
      return b;
    } catch {
      if (mine === seq.current) setHeld({ balance: null, scope });
      return null;
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [token, fetchBalance]);

  const setScopeProjectId = useCallback((id: string | null) => {
    setScopeProjectIdState((prev) => (prev === id ? prev : id));
  }, []);

  useEffect(() => {
    if (!token) { setHeld(null); return; }
    refresh();
  }, [token, scopeProjectId, refresh]);

  useEffect(() => {
    if (!turnRunning || !token) return;
    const t = setInterval(() => { refresh(); }, TURN_POLL_MS);
    return () => clearInterval(t);
  }, [turnRunning, token, refresh]);

  const settled = !!held && held.scope === scopeProjectId;
  const balance = settled ? held!.balance : null;
  // Loading until the CURRENT scope's answer is in, the first render
  // included: a null balance then means "failed", never "not asked yet".
  const busy = loading || (!!token && !settled);

  const value = useMemo<BillingContextValue>(() => ({
    balance,
    loading: busy,
    visible: billingVisible(balance, user),
    scopeProjectId,
    setScopeProjectId,
    setTurnRunning,
    refresh,
  }), [balance, busy, user, scopeProjectId, setScopeProjectId, refresh]);

  return <BillingContext.Provider value={value}>{children}</BillingContext.Provider>;
};
